import { useState } from "react";
import { CaretRightIcon } from "@phosphor-icons/react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const isDevEnvironment = import.meta.env.VITE_ENVIRONMENT === "dev";

interface ClientToolsPanelProps {
  runTimeTools: Record<
    string,
    {
      definition: { name: string; description: string; parameters: any };
      callback: (args: any) => Promise<string>;
      timeOutInMs?: number;
    }
  >;
  globalTools: Record<
    string,
    { callback: (args: any) => Promise<string>; timeOutInMs?: number }
  >;
  hasPreviewToolBound?: boolean;
}

/** Group tool names by their {namespace}-{method} convention. */
function groupByNamespace(names: string[]): Map<string, string[]> {
  const groups = new Map<string, string[]>();
  for (const name of names) {
    const match = /^([^-]+)-/.exec(name);
    const ns = match ? match[1] : "other";
    if (!groups.has(ns)) groups.set(ns, []);
    groups.get(ns)!.push(name);
  }
  return groups;
}

export function ClientToolsPanel({
  runTimeTools,
  globalTools,
  hasPreviewToolBound,
}: ClientToolsPanelProps) {
  const [expanded, setExpanded] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  if (!isDevEnvironment && !hasPreviewToolBound) return null;

  const runtimeEntries = Object.keys(runTimeTools);
  const globalEntries = Object.keys(globalTools);
  const totalCount = runtimeEntries.length + globalEntries.length;

  const runtimeGroups = groupByNamespace(runtimeEntries);
  const globalGroups = groupByNamespace(globalEntries);

  // Merge group names for display
  const allNamespaces = new Set([
    ...runtimeGroups.keys(),
    ...globalGroups.keys(),
  ]);

  const toggleGroup = (ns: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(ns)) next.delete(ns);
      else next.add(ns);
      return next;
    });
  };

  return (
    <section className="border-b border-border bg-muted/40 font-sans text-sm text-foreground">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setExpanded((p) => !p)}
        aria-expanded={expanded}
        className="w-full justify-start rounded-none px-3"
      >
        <CaretRightIcon
          className={`transition-transform ${expanded ? "rotate-90" : ""}`}
          aria-hidden="true"
        />
        Client Tools — {totalCount} tool{totalCount !== 1 ? "s" : ""} available
        <span className="ml-auto text-xs font-normal text-muted-foreground">
          {runtimeEntries.length} runtime, {globalEntries.length} global
        </span>
      </Button>

      {expanded && (
        <div className="px-3 pb-2 pt-1">
          {[...allNamespaces].sort().map((ns) => {
            const rtTools = runtimeGroups.get(ns) ?? [];
            const gTools = globalGroups.get(ns) ?? [];
            const nsCount = rtTools.length + gTools.length;
            const isOpen = expandedGroups.has(ns);

            return (
              <div key={ns} className="mb-1">
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={() => toggleGroup(ns)}
                  aria-expanded={isOpen}
                  className="w-full justify-start px-0 font-medium"
                >
                  <CaretRightIcon
                    className={`transition-transform ${isOpen ? "rotate-90" : ""}`}
                    aria-hidden="true"
                  />
                  {ns}
                  <span className="text-xs font-normal text-muted-foreground">
                    ({nsCount})
                  </span>
                </Button>

                {isOpen && (
                  <div className="pl-4">
                    {rtTools.map((name) => {
                      const tool = runTimeTools[name];
                      return (
                        <div
                          key={name}
                          className="border-b border-border py-0.5"
                        >
                          <div className="font-mono text-xs text-primary">
                            {name}
                          </div>
                          <div
                            className="truncate text-xs text-muted-foreground"
                            title={tool.definition.description}
                          >
                            {tool.definition.description}
                          </div>
                        </div>
                      );
                    })}
                    {gTools.map((name) => (
                      <div key={name} className="border-b border-border py-0.5">
                        <div className="flex items-center gap-1 font-mono text-xs text-success">
                          {name}
                          <Badge size="sm" variant="neutral" type="ghost">
                            global
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
